import React, {useEffect, useRef, useState} from 'react';
import {FaTrash} from 'react-icons/fa';
import Button from '../../../../../components/button/button.jsx'
import NoDataContent from "../../../../../components/reports/report-menu/no-data-content.jsx";
import ReportUtils from "../../../../../utils/report-utils.js";
import ModalImage from "../../../../../components/modal/modal-image.jsx";
import ImagePreparationProgress from "../../../../../components/image-preparation-progress/image-preparation-progress.jsx";
import {getMainImageKey, getMainImageUrl, getPlateImageKey, getPreparedImageSource, getPrintImageKeys, getPrintImagePlaceholder, preparePrintImages, revokeImageObjectUrls} from "../utils/image-print-utils.js";

const ReportContent = ({ data, reloadReportTrigger, setCurrentTotal, setCanPrint, showMoreInformation }) => {
    const formatFileSize = (bytes) => {
        if (typeof bytes !== "number") {
            return "Não disponível";
        }

        if (bytes < 1024) {
            return `${bytes} bytes`;
        }

        const sizeInKilobytes = bytes / 1024;

        if (sizeInKilobytes < 1024) {
            return `${sizeInKilobytes.toLocaleString("pt-BR", {maximumFractionDigits: 2})} KB`;
        }

        const sizeInMegabytes = sizeInKilobytes / 1024;

        return `${sizeInMegabytes.toLocaleString("pt-BR", {maximumFractionDigits: 2})} MB`;
    };

    const [total, setTotal] = useState(data?.total || 0);

    const [reloadTrigger, setReloadTrigger] = useState(false);
    const [originalItems, setOriginalItems] = useState(data?.data || []);
    const [items, setItems] = useState(data?.data || []);

    const [openImageModal, setOpenImageModal] = useState(false);
    const [selectedImage, setSelectedImage] = useState("");
    const [onErrorImage, setOnErrorImage] = useState({});

    const [preparedImageSources, setPreparedImageSources] = useState({});
    const [imageLoadRevision, setImageLoadRevision] = useState(0);
    const imageObjectUrlsRef = useRef({});
    const preparedImageSourcesRef = useRef({});
    const previousDataRef = useRef(data);
    const preservePreparedImagesRef = useRef(false);

    const isLoadingImagesRef = useRef(false);
    const createReloadRef = useRef(false);

    const showImages = data?.showimages;
    const showMonitoring = data?.showmonitoring;
    const showVehicleInfo = data?.showVehicleInformation;
    const qualityParam = data.highimagequality ? '?thumbnail=false' : '?thumbnail=true';

    useEffect(() => {
        ReportUtils.withoutContentReadjust();
        setReloadTrigger(false);
    }, [data, reloadTrigger]);

    const removeItem = (id) => {
        setItems(prev => {
            const updated = prev.filter(item => item.procid !== id);
            setCurrentTotal(updated.length);
            return updated;
        });

        setTotal(prev => prev - 1);
    };

    useEffect(() => {
        setItems(data?.data || []);
        setOriginalItems(data?.data || []);
        setTotal(data?.total || 0);

        if (previousDataRef.current === data) return;

        previousDataRef.current = data;
        preservePreparedImagesRef.current = false;
        setImageLoadRevision(revision => revision + 1);
    }, [data]);

    useEffect(() => {
        if (!createReloadRef.current) {
            createReloadRef.current = true;
            return;
        }

        setItems(originalItems);
        setTotal(data?.total);
        setReloadTrigger(true);

        setCurrentTotal(originalItems.length);

        const hasMissingImages = showImages && getPrintImageKeys(originalItems).some(key => !preparedImageSourcesRef.current[key]);

        if (hasMissingImages) {
            preservePreparedImagesRef.current = true;
            setImageLoadRevision(revision => revision + 1);
        }
    }, [reloadReportTrigger]);

    useEffect(() => {
        preparedImageSourcesRef.current = preparedImageSources;
    }, [preparedImageSources]);

    useEffect(() => {
        return () => revokeImageObjectUrls(imageObjectUrlsRef);
    }, []);

    useEffect(() => {
        return preparePrintImages({
            items,
            showImages,
            qualityParam,
            imageObjectUrlsRef,
            isLoadingImagesRef,
            setPreparedImageSources,
            setOnErrorImage,
            preparedImageSourcesRef,
            preservePreparedImages: preservePreparedImagesRef.current,
            onComplete: () => {
                preservePreparedImagesRef.current = false;
            }
        });
    }, [imageLoadRevision, showImages]);

    const subBar = (() => {
        if (!showVehicleInfo && !showMonitoring) return "h-[33px]!";
        if (!showVehicleInfo && showMonitoring) return "h-[32.75px]!";
        return "h-[32.78px]!";
    })();

    const imagesContainer = (() => {
        if (!showVehicleInfo && !showMonitoring) return "absolute inset-x-0 bottom-0 top-[34.3px]";
        if (!showVehicleInfo && showMonitoring) return "absolute inset-x-0 bottom-0 top-[32.75px]";
        return "absolute inset-x-0 bottom-0 top-[32.78px]";
    })();

    const imageLayoutClass = (() => {
        if (!showVehicleInfo && !showMonitoring) return "vehicle-traffic-layout-simple";
        if (!showVehicleInfo && showMonitoring) return "vehicle-traffic-layout-monitoring";
        return "vehicle-traffic-layout-default";
    })();

    const printImageKeys = showImages ? getPrintImageKeys(items) : [];
    const totalPrintImages = printImageKeys.length;
    const preparedPrintImages = printImageKeys.filter(key => preparedImageSources[key]).length;
    const isPreparingPrintImages = totalPrintImages > preparedPrintImages;

    useEffect(() => {
        setCanPrint?.(!isPreparingPrintImages);
    }, [isPreparingPrintImages, reloadReportTrigger])

    return (
        <>
            <div className="text-center pb-4 text-[15px]"> <span className={'font-bold'}>Filtro utilizado:</span> {data.filter} </div>

            <div className={`font-bold py-2 flex items-center gap-3 ${!data.obs && total > 0 && showImages ? "pl-2" : ""}`}>
                <span className={'text-[14px]'}>{ total === data.limit ? `Pesquisa atingiu limite de ${data.limit} passagens` : `Total de passagens: ${total}`}</span>
                <ImagePreparationProgress
                    loaded={preparedPrintImages}
                    total={totalPrintImages}
                    className="ml-auto"
                />
            </div>

            { data.obs && (
                <div className={`flex flex-col text-[15px] p-3 mt-2 mb-4 w-full border-2 border-gray-300 rounded-[5px] border-separate border-spacing-0 align-top`}>
                    <div dangerouslySetInnerHTML={{__html: data.obs}}/>
                </div>
            )}

            { total === 0  && (
                <NoDataContent reportData={data} data={total} customMessage={true}/>
            )}

            { showImages ? (
                <>

                    <table className="table vehicle-traffic-report-table w-full table-fixed !bg-white p-0">
                        <tbody>
                            { items.map((element) => {
                                const mainImageKey = getMainImageKey(element);
                                const mainImageUrl = getMainImageUrl(element, qualityParam);
                                const plateImageKey = getPlateImageKey(element);
                                const imageMetadata = element.image_metadata;
                                const imageResolution = imageMetadata
                                    ? `${imageMetadata.width} × ${imageMetadata.height} px`
                                    : "Não disponível";

                                return (
                                    <tr key={element.procid} id={element.procid}>
                                        <td className="!px-0 !pb-4 w-full !bg-white !border-b-0" colSpan={2}>
                                            <table className="vehicle-traffic-passage-table w-full border-2 border-gray-300 rounded-[5px] border-separate border-spacing-0 overflow-visible print:break-inside-avoid bg-white h-full">

                                                <tbody>

                                                <tr className={'print:overflow-visible!'}>
                                                    <th className="text-[#52525c] text-left w-[9%] border-0 p-2 bg-[#f6f6f6] rounded-tl-[5px]">Placa</th>
                                                    <td className="relative border-0 p-2 bg-[#f6f6f6]"><span className="font-bold">{element.plate}</span></td>

                                                    <td rowSpan={4 + (showVehicleInfo ? 3 : 0) + (showMonitoring ? 1 : 0) + (showMoreInformation ? 1 : 0)} className={`vehicle-traffic-image-cell ${imageLayoutClass} w-[40%] align-top p-2 print:pl-4 relative bg-white rounded-tr-[5px]`}>

                                                        <div className="vehicle-traffic-image-divider absolute! top-12 bottom-5 w-[2px] bg-[#d1d5dc] z-30!"/>
                                                        <div className={`vehicle-traffic-sub-bar absolute top-0 left-0 right-0 bg-[#f6f6f6] rounded-tr-[5px] ${subBar}`}/>

                                                        <Button target="danger" className="!absolute !rounded-[3px] top-0 right-0 h-[28px] print:hidden z-20"
                                                            onClick={() => removeItem(element.procid)} title="Remover passagem do relatório">
                                                            <FaTrash size={14}/>
                                                        </Button>

                                                        <div className={`vehicle-traffic-images-container ${imagesContainer} flex items-center justify-center gap-2 z-10 print:ml-[10px]! print:transform print:-translate-x-[1.2rem]`}>

                                                            <button
                                                                type="button"
                                                                className={`vehicle-traffic-image-button max-w-[220px] max-h-[124.4px] min-w-[220px] min-h-[124.4px] w-full h-full flex items-center justify-center ${preparedImageSources[mainImageKey] && !onErrorImage[mainImageKey] ? "cursor-pointer" : ""}`}
                                                                onClick={() => {
                                                                    if (!preparedImageSources[mainImageKey] || onErrorImage[mainImageKey]) return;
                                                                    setSelectedImage(mainImageUrl);
                                                                    setOpenImageModal(true);
                                                                }}
                                                            >

                                                                <img
                                                                    className="vehicle-traffic-image w-full h-full object-contain"
                                                                    onError={(e) => {
                                                                        e.currentTarget.src = getPrintImagePlaceholder();
                                                                        setOnErrorImage(prev => ({...prev, [`${element.procid}-main`]: true}));
                                                                    }}
                                                                    src={getPreparedImageSource(preparedImageSources, mainImageKey)}
                                                                    alt={'imagem da passagem'}
                                                                    loading="eager"
                                                                    decoding="sync"
                                                                />

                                                            </button>

                                                            { element.plate && (
                                                                <img
                                                                    className="max-w-[220px] max-h-[124.4px] print:max-w-[205px] print:max-h-[109.4px] print:min-w-[205px] print:min-h-[109.4px] w-full h-full object-contain"
                                                                    onError={(e) => {e.currentTarget.src = getPrintImagePlaceholder();}}
                                                                    src={getPreparedImageSource(preparedImageSources, plateImageKey)}
                                                                    alt="imagem da placa"
                                                                />
                                                            )}

                                                        </div>
                                                    </td>
                                                </tr>

                                                { showVehicleInfo && (
                                                    <>
                                                        <tr className={'border-0'}>
                                                            <th className={'text-[#52525c] text-left w-[15%]'}>Marca/Modelo</th>
                                                            <td>{element.vehicleBrandModel || "Não cadastrado"}</td>
                                                        </tr>

                                                        <tr className={'border-0'}>
                                                            <th className={'text-[#52525c] text-left w-[15%]'}>Ano do modelo</th>
                                                            <td>{element.vehicleModelYear || "Não cadastrado"}</td>
                                                        </tr>

                                                        <tr className={'border-0'}>
                                                            <th className={'text-[#52525c] text-left w-[15%]'}>Cor</th>
                                                            <td>{element.vehicleColor || "Não cadastrado"}</td>
                                                        </tr>
                                                    </>
                                                )}

                                                <tr className={'border-0'}>
                                                    <th className={'text-[#52525c] text-left w-[15%]'}>Data/Hora</th>
                                                    <td>{element.datetime}</td>
                                                </tr>

                                                <tr className={'border-0'}>
                                                    <th className={'text-[#52525c] text-left w-[15%]'}>Local</th>
                                                    <td>
                                                        <div className="flex w-fit break-all max-w-[625px] print:max-w-[416.86px] print:pr-6">
                                                            {element.locale}, Sentido: {element.route}, ID: {element.laneid}
                                                        </div>
                                                    </td>
                                                </tr>

                                                <tr className={'border-0'}>
                                                    <th className={'text-[#52525c] text-left w-[15%]'}>Coordenadas do local</th>
                                                    <td><span title={'Abrir no Street View'} className={'inner-link flex w-fit'} onClick={() => { window.open(`https://www.google.com/maps/@?api=1&map_action=pano&viewpoint=${element.lat},${element.lng}`, "_blank"); }}>{element.lat} {element.lat && element.lng ? ", " : ""} {element.lng}</span></td>
                                                </tr>

                                                { showMonitoring && (
                                                    <tr className={'text-left w-[15%] border-0'}>
                                                        <th className={'text-[#52525c]'}>Monitorado</th>
                                                        <td>{element.ismonitoring}</td>
                                                    </tr>
                                                )}

                                                { showMoreInformation && (
                                                    <tr>
                                                        <th className={'text-[#52525c] text-left'}>Metadados da imagem original armazenada pelo Sentry</th>
                                                        <td>
                                                            <div className="min-w-0 text-sm text-gray-700">
                                                                <p><strong className={"text-[#52525c]"}>ID:</strong> {element.procid ?? "Não disponível"}</p>
                                                                <p><strong className={"text-[#52525c]"}>Tamanho:</strong> {formatFileSize(imageMetadata?.size_in_bytes)}</p>
                                                                <p><strong className={"text-[#52525c]"}>Resolução:</strong> {imageResolution}</p>
                                                                <p className="break-all"><strong className={"text-[#52525c]"}>Hash SHA-256:</strong> {imageMetadata?.sha256 ?? "Não disponível"}</p>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                )}

                                                </tbody>
                                            </table>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>

                    <ModalImage
                        open={openImageModal}
                        setOpen={setOpenImageModal}
                        imgUrl={selectedImage}
                    />

                </>

            ) : (

                <table id="vehicle-traffic-data" className="table">
                    <thead>
                        <tr className={'text-left bg-[#f6f6f6]'}>
                            <th className={'w-[12%]'}>Data/Hora</th>
                            <th className={'w-[7%]'}>Placa</th>
                            <th className={'w-[71%]'}>Local</th>
                            { showMonitoring && ( <th className={'w-[10%]'}>Monitoramento</th> )}
                        </tr>
                    </thead>

                    <tbody>
                        { data.data.map((element, index) => (
                            <tr key={index} className={'!bg-white'}>
                                <td className={'w-[12%]'}>{element.datetime}</td>
                                <td className={'w-[7%]'} style={{ fontWeight: 700 }}>{element.plate}</td>

                                <td className={'w-[71%]'}>
                                    <div className="flex break-all">
                                        {element.locale}, Sentido: {element.route}, ID: {element.laneid}
                                    </div>
                                </td>

                                { showMonitoring && (
                                    <td className={'w-[10%]'}>{element.ismonitoring}</td>
                                )}

                            </tr>
                        ))}
                    </tbody>
                </table>

            )}
        </>
    );
};

export default ReportContent;
